import { TFunction } from 'i18next';

import type { OpenClassOrderConflict } from '../../openClasses/types/OpenClass';
import { describeHermanUserEmailError } from '../../utils/apiErrors';

export const getOpenClassBookingOrderPath = (id: number) =>
  `/bestallningar/open-class/${id}`;

type OrderScopeUser = {
  preferredSubsidiaryCompany?: { id: number };
};

type OrderScopeOrder = {
  clientCompany: { subsidiaryCompany: { id: number } } | null;
  subsidiaryCompany?: { id: number } | null;
};

export const getOpenClassOrderSubsidiaryCompanyId = (
  user: OrderScopeUser | null | undefined,
  { isNewOrder, order }: { isNewOrder: boolean; order?: OrderScopeOrder },
): number | undefined => {
  if (order?.subsidiaryCompany) return order.subsidiaryCompany.id;
  if (order?.clientCompany) return order.clientCompany.subsidiaryCompany.id;
  if (!isNewOrder) return undefined;
  return user?.preferredSubsidiaryCompany?.id;
};

type OpenClassOrderErrorLike = {
  response?: {
    status?: number;
    data?: {
      code?: string;
      conflicts?: OpenClassOrderConflict[];
    };
  };
};

// The most specific code, so callers can show an inline reason instead of a generic failure.
// Axios-free so this stays unit-testable without ESM transforms.
export const getOpenClassOrderConflictCode = (
  error: unknown,
): string | undefined => {
  const body = (error as OpenClassOrderErrorLike | null)?.response?.data;
  return body?.conflicts?.[0]?.code ?? body?.code;
};

// Nested registration endpoints answer with a bare status, so callers fall back to mapping that.
export const getOpenClassOrderErrorStatus = (
  error: unknown,
): number | undefined =>
  (error as OpenClassOrderErrorLike | null)?.response?.status;

// A runtime code is never one of the key literals i18next is typed against, so widen once here
// rather than cast at every call site.
type TranslateKey = (key: string, options?: { defaultValue: string }) => string;

// The statically known key literals, minus the array/template forms `t` also accepts.
type TranslationKey = Extract<Parameters<TFunction>[0], string>;

// Every write fails the same way, a structured `{ code }` when the backend knows why and a bare
// status when it doesn't. Which key wins is decided here so the call sites can't drift apart.
export const describeOpenClassOrderError = (
  error: unknown,
  t: TFunction,
  fallbackKey: TranslationKey,
): string => {
  const translate = t as TranslateKey;
  const fallback = translate(fallbackKey);
  const hermanUserEmailError = describeHermanUserEmailError(error, t);
  if (hermanUserEmailError) return hermanUserEmailError;

  const code = getOpenClassOrderConflictCode(error);
  if (code) {
    return translate(`views.OpenClassBookingOrderDetails.conflicts.${code}`, {
      defaultValue: fallback,
    });
  }
  switch (getOpenClassOrderErrorStatus(error)) {
    case 409:
      return translate(
        'views.OpenClassBookingOrderDetails.registrationConflict',
        { defaultValue: fallback },
      );
    case 422:
      return translate(
        'views.OpenClassBookingOrderDetails.invalidRegistration',
        { defaultValue: fallback },
      );
    case 404:
      return translate(
        'views.OpenClassBookingOrderDetails.conflicts.resourceNotFound',
        { defaultValue: fallback },
      );
    default:
      return fallback;
  }
};
